import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { Download, Eye, Loader2, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { DataTable } from '@/components/data-table/DataTable';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { AclDenied } from '@/components/feedback/AclDenied';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_CALL_QUALITY_FORM,
  API_CALL_QUALITY_SUBMISSIONS,
  API_CALL_QUALITY_SUBMISSIONS_EXPORT,
} from '@/service/url';
import { BACKEND_API_BASE_URL, USER_ROLES } from '@/utilities/constant';
import { formatDateTime } from '@/utilities/helpers';
import { fetchAuthedBlobUrl } from '@/utilities/mediaAudio';
import { getUser } from '@/utilities/setting';

import {
  canCallQualityFeedback,
  FeedbackDetail,
  issuesText,
  StarRow,
  type CallQualityFeedback,
  type CallQualityForm,
  type FeedbackSection,
} from './feedback';

const MAX_MONTHS_BACK = 3;
const PAGE_SIZE = 50;

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function rangeBounds() {
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const min = new Date(now);
  min.setMonth(min.getMonth() - MAX_MONTHS_BACK);
  return { weekStart: ymd(weekStart), today: ymd(now), min: ymd(min) };
}

function withKeys<T extends { key?: string }>(items: T[], labelOf: (t: T) => string, prefix: string): (T & { key: string })[] {
  const used = new Set<string>();
  return items.map((item, i) => {
    let key = item.key || labelOf(item).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 36) || `${prefix}_${i + 1}`;
    while (used.has(key)) key = `${key.slice(0, 36)}_${i + 1}`;
    used.add(key);
    return { ...item, key };
  });
}

const EMPTY_FORM: CallQualityForm = {
  name: 'Call audio',
  rating_question: 'How was the call audio?',
  rating_required: true,
  threshold: 3,
  sections: [],
  comment_enabled: true,
  comment_required: false,
};

// ---------- form preview ----------

function FormPreview({ form }: { form: CallQualityForm }) {
  return (
    <div className="space-y-3 text-sm">
      <div>
        <div className="font-medium">
          {form.rating_question} {form.rating_required && <span className="text-destructive">*</span>}
        </div>
        <StarRow value={null} size={20} className="mt-1" />
      </div>
      {form.sections.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">
            Shown when the rating is {form.threshold} star{form.threshold > 1 ? 's' : ''} or lower — "What went wrong?"
          </div>
          {form.sections.map((s) => (
            <div key={s.key} className="rounded-md border p-2">
              <div className="mb-1 text-xs font-medium">
                {s.title} {s.required && <span className="text-destructive">*</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {s.options.map((o) => (
                  <Badge key={o.key} variant="outline">
                    {o.label}
                  </Badge>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="text-xs text-muted-foreground">
        Comment: {form.comment_enabled ? (form.comment_required ? 'required' : 'optional') : 'off'}
      </div>
    </div>
  );
}

// ---------- superadmin editor ----------

interface DraftSection {
  key?: string;
  title: string;
  required: boolean;
  optionsText: string; // one option label per line
}

function FormEditor({
  open,
  form,
  onClose,
  onSaved,
}: {
  open: boolean;
  form: CallQualityForm | null;
  onClose: () => void;
  onSaved: (f: CallQualityForm) => void;
}) {
  const [draft, setDraft] = useState<CallQualityForm>(EMPTY_FORM);
  const [sections, setSections] = useState<DraftSection[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = form || EMPTY_FORM;
    setDraft(base);
    setSections(
      base.sections.map((s) => ({ key: s.key, title: s.title, required: !!s.required, optionsText: s.options.map((o) => o.label).join('\n') })),
    );
  }, [open, form]);

  const set = <K extends keyof CallQualityForm>(k: K, v: CallQualityForm[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const setSection = (i: number, patch: Partial<DraftSection>) =>
    setSections((list) => list.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const save = async () => {
    const existing = new Map((form?.sections || []).map((s) => [s.key, s]));
    const built: FeedbackSection[] = withKeys(sections, (s) => s.title, 'section').map((s) => {
      // Keep keys of options whose label did not change, so pending answers still match.
      const oldOptions = new Map((existing.get(s.key)?.options || []).map((o) => [o.label, o.key]));
      const labels = s.optionsText.split('\n').map((l) => l.trim()).filter(Boolean);
      return {
        key: s.key,
        title: s.title.trim(),
        required: s.required,
        options: withKeys(labels.map((label) => ({ key: oldOptions.get(label), label })), (o) => o.label, 'option'),
      };
    });
    setSaving(true);
    try {
      const saved = await postApiCall(API_CALL_QUALITY_FORM, { ...draft, sections: built });
      toast.success('Call audio form saved');
      onSaved(saved);
      onClose();
    } catch {
      /* httpCall already toasted the reason */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{form ? 'Edit' : 'Set up'} call audio form</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="cqf-name">Form name *</Label>
              <Input id="cqf-name" maxLength={100} value={draft.name} onChange={(e) => set('name', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cqf-threshold">Ask "What went wrong?" at or below</Label>
              <select
                id="cqf-threshold"
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                value={draft.threshold}
                onChange={(e) => set('threshold', Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n} star{n > 1 ? 's' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="cqf-question">Rating question *</Label>
            <Input id="cqf-question" maxLength={200} value={draft.rating_question} onChange={(e) => set('rating_question', e.target.value)} />
          </div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={draft.rating_required} onCheckedChange={(v) => set('rating_required', v)} /> Rating required
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={draft.comment_enabled} onCheckedChange={(v) => set('comment_enabled', v)} /> Comment box
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={draft.comment_enabled && draft.comment_required}
                disabled={!draft.comment_enabled}
                onCheckedChange={(v) => set('comment_required', v)}
              />{' '}
              Comment required
            </label>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>"What went wrong?" sections ({sections.length}/10)</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={sections.length >= 10}
                onClick={() => setSections((l) => [...l, { title: '', required: false, optionsText: '' }])}
              >
                <Plus className="mr-1 h-4 w-4" /> Section
              </Button>
            </div>
            {sections.map((s, i) => (
              <div key={i} className="space-y-2 rounded-md border p-3">
                <div className="flex items-center gap-2">
                  <Input
                    aria-label={`Section ${i + 1} title`}
                    placeholder="Section title, e.g. Audio"
                    maxLength={60}
                    value={s.title}
                    onChange={(e) => setSection(i, { title: e.target.value })}
                  />
                  <label className="flex shrink-0 items-center gap-2 text-xs">
                    <Switch checked={s.required} onCheckedChange={(v) => setSection(i, { required: v })} /> Required
                  </label>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label="Remove section"
                    onClick={() => setSections((l) => l.filter((_, idx) => idx !== i))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <Textarea
                  aria-label={`Section ${i + 1} options`}
                  rows={3}
                  placeholder={'One option per line, e.g.\nThere was echo\nSound was delayed'}
                  value={s.optionsText}
                  onChange={(e) => setSection(i, { optionsText: e.target.value })}
                />
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- page ----------

export default function CallQualityFeedbackPage() {
  const isSuperAdmin = getUser()?.role === USER_ROLES.SUPERADMIN;
  const canView = canCallQualityFeedback('view_permission');
  const canRead = canCallQualityFeedback('read');

  const [form, setForm] = useState<CallQualityForm | null>(null);
  const [formLoading, setFormLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const bounds = useMemo(rangeBounds, []);
  const [from, setFrom] = usePersistedState('call-quality-feedback/CallQualityFeedback:from', bounds.weekStart);
  const [to, setTo] = usePersistedState('call-quality-feedback/CallQualityFeedback:to', bounds.today);
  // Saved dates can fall outside the allowed window as time passes: pull them back in.
  useEffect(() => {
    if (from < bounds.min) setFrom(bounds.min);
    if (to > bounds.today) setTo(bounds.today);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [maxRating, setMaxRating] = usePersistedState('call-quality-feedback/CallQualityFeedback:maxRating', '');
  const [agent, setAgent] = usePersistedState<{ id: number; name: string } | null>('call-quality-feedback/CallQualityFeedback:agent', null);
  const [rows, setRows] = useState<CallQualityFeedback[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [viewing, setViewing] = useState<CallQualityFeedback | null>(null);
  const reqRef = useRef(0);

  const filters = useMemo(
    () => ({
      from_date: from,
      to_date: to,
      ...(maxRating ? { max_rating: maxRating } : {}),
      ...(agent ? { agent_id: agent.id } : {}),
    }),
    [from, to, maxRating, agent],
  );

  useEffect(() => {
    if (!canView && !canRead) return;
    getApiCall(API_CALL_QUALITY_FORM, undefined)
      .then((f: CallQualityForm | null) => setForm(f || null))
      .catch(() => {})
      .finally(() => setFormLoading(false));
  }, [canView, canRead]);

  const load = useCallback(
    (after: string | null) => {
      const id = ++reqRef.current;
      setLoading(true);
      getApiCall(API_CALL_QUALITY_SUBMISSIONS, { ...filters, limit: PAGE_SIZE, ...(after ? { cursor: after } : {}) })
        .then((data: { rows: CallQualityFeedback[]; next_cursor: string | null }) => {
          if (id !== reqRef.current) return;
          setRows((prev) => (after ? [...prev, ...data.rows] : data.rows));
          setCursor(data.next_cursor);
        })
        .catch(() => {})
        .finally(() => id === reqRef.current && setLoading(false));
    },
    [filters],
  );

  useEffect(() => {
    if (canRead) load(null);
  }, [canRead, load]);

  const changeRange = (nextFrom: string, nextTo: string) => {
    let f = nextFrom < bounds.min ? bounds.min : nextFrom;
    let t = nextTo > bounds.today ? bounds.today : nextTo;
    if (f > t) {
      if (nextFrom !== from) t = f;
      else f = t;
    }
    setFrom(f);
    setTo(t);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(Object.entries(filters).map(([k, v]) => [k, String(v)])).toString();
      const { objectUrl } = await fetchAuthedBlobUrl(`${BACKEND_API_BASE_URL}${API_CALL_QUALITY_SUBMISSIONS_EXPORT}?${qs}`);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `call-quality-feedback-${from}-to-${to}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch (e: any) {
      toast.error(e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<ColumnDef<CallQualityFeedback>[]>(
    () => [
      {
        id: 'submitted_at',
        header: 'Submitted',
        accessorFn: (r) => r.submitted_at,
        cell: ({ row }) => <span className="whitespace-nowrap text-xs">{formatDateTime(row.original.submitted_at)}</span>,
      },
      {
        id: 'agent',
        header: 'Agent',
        accessorFn: (r) => `${r.agent_name || ''} ${r.agent_number || ''}`,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <button
              type="button"
              className="text-left text-xs hover:underline"
              title="Show only this agent"
              onClick={() => r.agent_id && setAgent({ id: r.agent_id, name: r.agent_name || `#${r.agent_id}` })}
            >
              <div className="font-medium">{r.agent_name || '-'}</div>
              <div className="text-muted-foreground">{r.agent_number || ''}</div>
            </button>
          );
        },
      },
      {
        id: 'call',
        header: 'Call',
        accessorFn: (r) => `${r.customer_number || ''} ${r.trace_id || ''}`,
        cell: ({ row }) => (
          <div className="text-xs">
            <div>
              <span className="text-muted-foreground">Customer:</span> {row.original.customer_number || '-'}
            </div>
            <div className="max-w-[220px] truncate" title={row.original.trace_id || ''}>
              <span className="text-muted-foreground">Trace:</span> {row.original.trace_id || '-'}
            </div>
          </div>
        ),
      },
      {
        id: 'rating',
        header: 'Rating',
        accessorFn: (r) => r.rating ?? 0,
        cell: ({ row }) => <StarRow value={row.original.rating} />,
      },
      {
        id: 'issues',
        header: 'Issues',
        accessorFn: (r) => issuesText(r.issues),
        cell: ({ row }) => (
          <div className="max-w-[280px] truncate text-xs" title={issuesText(row.original.issues)}>
            {issuesText(row.original.issues) || '-'}
          </div>
        ),
      },
      {
        id: 'comment',
        header: 'Comment',
        accessorFn: (r) => r.comment || '',
        cell: ({ row }) => (
          <div className="max-w-[220px] truncate text-xs" title={row.original.comment || ''}>
            {row.original.comment || '-'}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button size="icon" variant="ghost" aria-label="View feedback" onClick={() => setViewing(row.original)}>
            <Eye className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [],
  );

  if (!canView && !canRead) return <AclDenied operation="view" />;

  return (
    <ScrollPage>
      <PageHeader
        title="Call Audio Feedback"
        description="Agents rate each call's audio after submitting its disposition. Feedback never affects calls."
        actions={
          isSuperAdmin && (
            <Button onClick={() => setEditing(true)} disabled={formLoading}>
              <Pencil className="mr-1 h-4 w-4" /> {form ? 'Edit form' : 'Set up form'}
            </Button>
          )
        }
      />

      <section className="rounded-lg border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">{form?.name || 'Form'}</h2>
          {form?.updated_at && <span className="text-xs text-muted-foreground">Updated {formatDateTime(form.updated_at)}</span>}
        </div>
        {formLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : form ? (
          <FormPreview form={form} />
        ) : (
          <p className="text-sm text-muted-foreground">
            The form has not been set up yet{isSuperAdmin ? '.' : ' by the superadmin.'} Agents are not asked for feedback until it is.
          </p>
        )}
      </section>

      {canRead && (
        <section className="space-y-3">
          <h2 className="font-semibold">Submissions</h2>
          <DataTable
            tableId="call-quality-feedback-submissions"
            columns={columns}
            data={rows}
            isLoading={loading && rows.length === 0}
            pageSize={25}
            getRowId={(r) => String(r.id)}
            emptyTitle="No feedback in this date range"
            disableExport
            leftContent={
              <DateRangeFilter
                compact
                value={{ from, to }}
                onChange={(next) => changeRange(next.from, next.to)}
                min={bounds.min}
                max={bounds.today}
                disabled={loading}
                actions={
                  <>
                    <Button size="icon" variant="outline" className="h-9 w-9" aria-label="Refresh" disabled={loading} onClick={() => load(null)}>
                      <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                    </Button>
                    <Button variant="outline" className="h-9" disabled={exporting || rows.length === 0} onClick={exportCsv}>
                      {exporting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Export
                    </Button>
                  </>
                }
              >
                <div>
                  <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Rating</label>
                  <select
                    aria-label="Rating filter"
                    className="h-9 rounded-md border bg-background px-2 text-sm"
                    value={maxRating}
                    onChange={(e) => setMaxRating(e.target.value)}
                  >
                    <option value="">All ratings</option>
                    {[1, 2, 3, 4].map((n) => (
                      <option key={n} value={n}>
                        {n} star{n > 1 ? 's' : ''} or lower
                      </option>
                    ))}
                  </select>
                </div>
                {agent && (
                  <Badge variant="secondary" className="mb-2 gap-1">
                    Agent: {agent.name}
                    <button type="button" aria-label="Clear agent filter" onClick={() => setAgent(null)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
              </DateRangeFilter>
            }
          />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {rows.length} loaded{cursor ? ', more available' : ''} · Export includes every row in the range (up to 100,000)
            </span>
            {cursor && (
              <Button size="sm" variant="outline" disabled={loading} onClick={() => load(cursor)}>
                {loading && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Load more
              </Button>
            )}
          </div>
        </section>
      )}

      {isSuperAdmin && <FormEditor open={editing} form={form} onClose={() => setEditing(false)} onSaved={setForm} />}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Call audio feedback</DialogTitle>
          </DialogHeader>
          {viewing && <FeedbackDetail list={[viewing]} showCustomer />}
          {/* {viewing?.trace_id && <p className="break-all text-xs text-muted-foreground">Trace ID: {viewing.trace_id}</p>} */}
        </DialogContent>
      </Dialog>
    </ScrollPage>
  );
}
